import { Category } from "@shared/types";
import { gql, useQuery } from "@shared/utils";
import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import React, { FunctionComponent, useMemo, useState } from "react";

import { useGqlField } from "../../../../hooks";
import VariablePopup from "../DynamicVariables/VariablePopup";

import { DVInlineProps } from "./types";
import { VariableButton } from "./VariableButton";

const EDIT_CATEGORY_ID = gql`
  mutation UpdateDynamicVariable($id: String!, $value: String!) {
    updateDynamicVariable(input: { id: $id, categoryId: $value }) {
      _id
      categoryId
    }
  }
`;
const EDIT_OUTPUT = gql`
  mutation UpdateDynamicVariable($id: String!, $value: String!) {
    updateDynamicVariable(input: { id: $id, output: $value }) {
      _id
      output
    }
  }
`;
const CATEGORYS = gql`
  query Categorys($productId: String!) {
    categorys(product: $productId) {
      _id
      name
    }
  }
`;
const CATEGORY = gql`
  query Category($id: ID!) {
    category(_id: $id) {
      _id
      name
      Product {
        ParentProduct {
          Categories {
            _id
            name
          }
        }
      }
    }
  }
`;

const outputs: Array<{ label: string; value: string }> = [
  {
    value: "EXISTS",
    label: "Exists",
  },
  {
    value: "COMPUTED_QUANTITY",
    label: "Computed Quantity",
  },
];

export const DVInlineCategory: FunctionComponent<DVInlineProps> = ({
  dynamicVariable,
  productId: productIdTrue,
  parentProductId,
  onVariableDelete,
  removeDynamicVariable,
  activeDynamicVariableId,
  setActiveDynamicVariableId,
  onChange,
  variables,
}) => {
  const [open, setOpen] = useState(false);

  const fromParent = useMemo(() => {
    return /^PARENT/.test(dynamicVariable.variableType);
  }, [dynamicVariable.variableType]);
  const productId = useMemo(() => {
    return fromParent ? parentProductId : productIdTrue;
  }, [fromParent, parentProductId, productIdTrue]);

  const { data } = useQuery<{
    category?: Category;
  }>(CATEGORY, { variables: { id: dynamicVariable.categoryId } });

  const [categoryId, setCategoryId] = useGqlField(
    dynamicVariable.categoryId,
    dynamicVariable._id,
    EDIT_CATEGORY_ID
  );
  const [output, setOutput] = useGqlField(
    dynamicVariable.output,
    dynamicVariable._id,
    EDIT_OUTPUT
  );

  const { data: categoryData } = useQuery<{
    categorys?: Array<Category>;
  }>(CATEGORYS, { variables: { productId }, fetchPolicy: 'cache-and-network' });

  const categorys: Array<Category> = [...(categoryData?.categorys || [])].sort(
    (a, z) => (a.name > z.name ? 1 : -1)
  );
  const onModalClose = async () => {
    if (categoryId === "") {
      variables &&
        onChange?.(variables.filter((v) => v !== dynamicVariable._id));
      await removeDynamicVariable?.({
        variables: {
          input: {
            id: dynamicVariable._id,
          },
        },
      });
      onVariableDelete?.(dynamicVariable._id);
    }
  };
  return (
    <VariablePopup
      actions={[]}
      title="Edit Component Variable"
      onCancel={() => {
        setOpen(false);
        onModalClose();
      }}
      onSave={() => {
        setOpen(false);
      }}
      onClose={() => {
        setOpen(false);
      }}
      forceOpen={open}
      dynamicVariable={dynamicVariable}
      activeDynamicVariableId={activeDynamicVariableId}
      setActiveDynamicVariableId={setActiveDynamicVariableId}
    >
      <VariableButton
        typeLabel={fromParent ? "Parent Cat" : "Category"}
        variableType={dynamicVariable.variableType}
        onClick={() => {
          setOpen(true);
        }}
        line1={data?.category?.name}
        output={dynamicVariable?.output}
        fromParent={fromParent}
      />
      <Box>
        {categorys && (
          <FormControl fullWidth sx={{ marginTop: 2 }}>
            <InputLabel>Component</InputLabel>
            <Select
              label="Component"
              variant="outlined"
              onChange={(e, _v) => {
                const categoryIds = categorys.map((t) => t._id);
                categoryIds.includes(e.target.value) &&
                  setCategoryId(e.target.value);
              }}
              value={categoryId}
            >
              {categorys?.map((t: { _id: string; name: string }) => (
                <MenuItem value={t._id} key={t._id}>
                  {t._id === categoryId ? "•" : ""} {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControl fullWidth sx={{ marginTop: 2 }}>
          <InputLabel>Output</InputLabel>
          <Select
            label="Output"
            variant="outlined"
            onChange={(e, _v) => {
              setOutput(e.target.value);
            }}
            value={output}
          >
            {outputs.map((o: { label: string; value: string }) => {
              return (
                <MenuItem value={o.value} key={o.value}>
                  {o.label}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Box>
    </VariablePopup>
  );
};
